/**
 * Report 109 (AIM Meal Pattern Changes) from the AIM diet update dialog (AimDietUpdateDialog's Print, as the
 * desktop prints it from frmDietech_AutoItemModification_Summary.mbtnPrint_ItemClick).
 *
 * The AIM run (`POST aim-diet/run`) returns the change set as the report reads it (`reportDataXml`, with
 * `reportDataXmlSchema` only when it has child rows) and, since the 2026-10 redesign, the report's context box
 * (`reportContext`: the resident's unit and room, the diet order before and after, when AIM ran and who ran it).
 * This builds the queue request with all of it, the way the desktop summary's Print fills AIMMPChangesSettings.
 * A context field the server left empty is sent as empty / null: the report then leaves that line out.
 */
import type { QueueReportRequest } from '../../api/reportApi';
import type { AimReportContext } from '../../api/aimDietApi';

export type { AimReportContext };

/** The parts of the AIM run response the report needs (`AimDietRunResponse`). */
export interface MpChangesRun {
  reportDataXml?: string | null;
  reportDataXmlSchema?: string | null;
  reportContext?: AimReportContext | null;
}

const text = (v: string | null | undefined): string => (v ?? '').trim();
const date = (v: string | null | undefined): string | null => (v && v.trim() ? v : null);

/** AIMMPChangesSettings for report 109: the change set, the context, no saved grid layouts (the report has one layout). */
export function buildMpChangesReportSettings(
  run: MpChangesRun,
  args: { facilityId: number; patientId: number; dateFor: string; residentFullName?: string },
): Record<string, unknown> {
  const ctx = run.reportContext ?? {};
  return {
    fKey_Facility: args.facilityId,
    fKey_Patient: args.patientId,
    dateFor: args.dateFor,
    residentFullName: text(ctx.residentFullName) || text(args.residentFullName),
    strMasterLayout: '',
    strDetailLayout: '',
    strDataXML: run.reportDataXml ?? '',
    strDataXMLSchema: run.reportDataXmlSchema ?? '',
    oldDietOrder: text(ctx.oldDietOrder),
    newDietOrder: text(ctx.newDietOrder),
    dietOrderEffectiveDate: date(ctx.dietOrderEffectiveDate),
    unit: text(ctx.unit),
    room: text(ctx.room),
    aimRunDateTime: date(ctx.aimRunDateTime),
    aimRunBy: text(ctx.aimRunBy),
  };
}

/** True when the run has a change set to print. */
export function canPrintMpChanges(run: MpChangesRun | null | undefined): boolean {
  return !!run?.reportDataXml;
}

/** The queue request for report 109. */
export function mpChangesReportRequest(
  run: MpChangesRun,
  args: { facilityId: number; patientId: number; userId: number; dateFor: string; residentFullName?: string },
): QueueReportRequest {
  return {
    reportType: 'AIMMPChanges',
    fKey_User: args.userId,
    fKey_RS_Report: 109,
    settingsJson: JSON.stringify(buildMpChangesReportSettings(run, args)),
    reportName: 'Meal Pattern Changes',
    dateFor: args.dateFor,
  };
}
